import { useCallback, useEffect, useState } from 'react';

import * as Sentry from '@sentry/react';

import { vnextApiClient } from '@/services/vnext-api';
import type { BackofficeFile } from '@/types/report';

export interface UseBackofficeFilesResult {
  files: BackofficeFile[];
  isLoading: boolean;
  error: Error | null;
  isSelecting: boolean;
  selectFile: (rawDataKey: string) => Promise<void>;
  refetch: () => Promise<void>;
}

export function useBackofficeFiles(reportId: string | null): UseBackofficeFilesResult {
  const [files, setFiles] = useState<BackofficeFile[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [isSelecting, setIsSelecting] = useState(false);

  const refetch = useCallback(async () => {
    if (!reportId) {
      setFiles([]);
      return;
    }
    setIsLoading(true);
    setError(null);
    try {
      setFiles(await vnextApiClient.getBackofficeFiles(reportId));
    } catch (err) {
      const e = err instanceof Error ? err : new Error(String(err));
      Sentry.captureException(e);
      setError(e);
    } finally {
      setIsLoading(false);
    }
  }, [reportId]);

  useEffect(() => {
    void refetch();
  }, [refetch]);

  const selectFile = useCallback(
    async (rawDataKey: string) => {
      if (!reportId) {
        return;
      }
      setIsSelecting(true);
      try {
        await vnextApiClient.selectReportFile(reportId, rawDataKey);
      } catch (err) {
        const e = err instanceof Error ? err : new Error(String(err));
        Sentry.captureException(e);
        throw e;
      } finally {
        setIsSelecting(false);
      }
    },
    [reportId]
  );

  return { files, isLoading, error, isSelecting, selectFile, refetch };
}
